import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Search, Filter, Building2, Users, TrendingUp, MessageSquare, LogOut, UserCog, Activity, Trash2, MapPin, Sparkles, Send, MessageCircle, FileEdit, DollarSign, Trophy, RefreshCw, Globe2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "../services/api";

const Skeleton = ({ className = '' }) => (
  <div className={`bg-zinc-100 animate-pulse rounded ${className}`} />
);

const KpiCard = ({ label, value, sub, icon: Icon, borderColor = 'border-zinc-200', iconBg = 'bg-zinc-50', iconColor = 'text-zinc-400', trend, loading }) => (
  <Card className={`p-4 bg-white border ${borderColor}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-zinc-500 mb-1">{label}</p>
        {loading
          ? <Skeleton className="h-7 w-16 mb-1" />
          : <p className="text-2xl font-semibold text-zinc-900 truncate">{value ?? '—'}</p>
        }
        {sub && !loading && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
        {trend != null && !loading && (
          <p className={`text-xs mt-0.5 ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? '+' : ''}{trend}% vs last month
          </p>
        )}
      </div>
      <div className={`p-2.5 rounded-lg shrink-0 ml-3 ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
    </div>
  </Card>
);

const STATUS_COLORS = {
  "New": "bg-blue-100 text-blue-700 border-blue-200",
  "Contacted": "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Follow-up": "bg-orange-100 text-orange-700 border-orange-200",
  "Qualified": "bg-purple-100 text-purple-700 border-purple-200",
  "Won": "bg-green-100 text-green-700 border-green-200",
  "Lost": "bg-red-100 text-red-700 border-red-200",
};

const TIER_COLORS = {
  "TIER 1": "bg-amber-100 text-amber-800 border-amber-300",
  "TIER 2": "bg-sky-100 text-sky-800 border-sky-300",
  "TIER 3": "bg-slate-100 text-slate-700 border-slate-300",
};

// Outreach indicator: derived from the summary for a given lead
const getOutreachIndicator = (summary) => {
  if (!summary || summary.count === 0) return null;
  if (summary.has_replied)
    return { icon: MessageCircle, label: `Replied (${summary.count})`, className: "bg-green-100 text-green-700 border-green-300" };
  if (summary.latest_status === 'Sent')
    return { icon: Send, label: `Sent (${summary.count})`, className: "bg-blue-100 text-blue-700 border-blue-300" };
  // Drafted but not yet sent
  return { icon: FileEdit, label: `Drafted (${summary.count})`, className: "bg-gray-100 text-gray-600 border-gray-300" };
};

const Dashboard = ({ user, onLogout }) => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formatFilter, setFormatFilter] = useState("all"); // 'all' | 'supreme' | 'v2'
  const [isDragging, setIsDragging] = useState(false);
  const [outreachSummary, setOutreachSummary] = useState({}); // lead_id → { count, latest_status, has_replied }
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchLeads = async () => {
    try {
      const response = await api.get('/api/leads');
      setLeads(Array.isArray(response.data) ? response.data : []);
      setFilteredLeads(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching leads:", err);
      setError("Failed to fetch leads");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/stats');
      setStats(response.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
      setError("Could not load stats — check that the backend is running.");
    }
  };

  const fetchOutreachSummary = async () => {
    try {
      const response = await api.get('/api/outreach/summary');
      setOutreachSummary(response.data);
    } catch (err) {
      console.error("Error fetching outreach summary:", err);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchOutreachSummary();
  }, []);

  useEffect(() => {
    let filtered = leads;

    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    if (formatFilter === "supreme") {
      filtered = filtered.filter((lead) => lead.lead_format === "supreme");
    } else if (formatFilter === "v2") {
      filtered = filtered.filter((lead) => lead.lead_format !== "supreme");
    }

    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, formatFilter, leads]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      if (file.name.endsWith('.md')) formData.append("files", file);
    });

    try {
      const response = await api.post('/api/leads/upload', formData);
      const { leads_added, outreach_added } = response.data;
      // Informative toast that mentions what was parsed
      const parts = [];
      if (leads_added > 0) parts.push(`${leads_added} lead${leads_added !== 1 ? 's' : ''}`);
      if (outreach_added > 0) parts.push(`${outreach_added} outreach record${outreach_added !== 1 ? 's' : ''}`);
      if (parts.length > 0) {
        toast.success(`Imported ${parts.join(' and ')}`, { description: 'Both V2 and SUPREME formats supported' });
      } else {
        toast.info('No new records added — duplicates were skipped.');
      }
      fetchLeads();
      fetchStats();
      fetchOutreachSummary();
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Failed to upload files", { description: "Make sure your .md file is in V2 or SUPREME format." });
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); handleFileUpload(e.dataTransfer.files); };
  const handleFileInputChange = (e) => handleFileUpload(e.target.files);

  const deleteLead = async (leadId, companyName) => {
    try {
      await api.delete(`/api/leads/${leadId}`);
      toast.success(`Deleted ${companyName}`);
      fetchLeads();
      fetchStats();
      fetchOutreachSummary();
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Failed to delete lead");
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-zinc-900">Dashboard</h1>
        <p className="text-xs text-zinc-500 mt-0.5">Welcome back, {user?.name || user?.email}</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <AlertCircle size={15} />
          Could not load stats — check that the backend is running on port 8000.
        </div>
      )}

      {/* ── Row 1: Pipeline KPIs ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard loading={loading} label="Total Leads"
          value={stats?.total_leads}
          trend={stats?.trends?.new_leads}
          icon={Building2} borderColor="border-zinc-200" iconBg="bg-blue-50" iconColor="text-blue-600" />
        <KpiCard loading={loading} label="Pipeline Value"
          value={stats?.pipeline_value > 0 ? `$${stats.pipeline_value.toLocaleString()}` : '—'}
          sub="Proposal + Negotiating"
          icon={DollarSign} borderColor="border-orange-200" iconBg="bg-orange-50" iconColor="text-orange-500" />
        <KpiCard loading={loading} label="Won This Month"
          value={stats?.won_this_month ?? 0}
          trend={stats?.trends?.won}
          icon={Trophy} borderColor="border-emerald-200" iconBg="bg-emerald-50" iconColor="text-emerald-600" />
        <KpiCard loading={loading} label="Retainer MRR"
          value={stats?.mrr > 0 ? `$${stats.mrr.toLocaleString()}` : '—'}
          sub="Active retainers"
          icon={RefreshCw} borderColor="border-teal-200" iconBg="bg-teal-50" iconColor="text-teal-600" />
      </div>

      {/* ── Row 2: Breakdown ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard loading={loading} label="ICP-1 International"
          value={stats?.icp1_count ?? 0}
          icon={Globe2} borderColor="border-indigo-200" iconBg="bg-indigo-50" iconColor="text-indigo-500" />
        <KpiCard loading={loading} label="ICP-2 India SMB"
          value={stats?.icp2_count ?? 0}
          icon={Users} borderColor="border-rose-200" iconBg="bg-rose-50" iconColor="text-rose-500" />
        <KpiCard loading={loading} label="Calls Booked"
          value={stats?.status_counts?.['Call Booked'] ?? 0}
          icon={TrendingUp} borderColor="border-cyan-200" iconBg="bg-cyan-50" iconColor="text-cyan-600" />
        <KpiCard loading={loading} label="Outreach Sent"
          value={stats?.total_outreach ?? 0}
          icon={MessageSquare} borderColor="border-purple-200" iconBg="bg-purple-50" iconColor="text-purple-600" />
        <KpiCard loading={loading} label="SUPREME Leads"
          value={stats?.supreme_leads ?? 0}
          icon={Sparkles} borderColor="border-violet-200" iconBg="bg-violet-50" iconColor="text-violet-500" />
      </div>

        {/* ── Drag & Drop Zone ─────────────────────────────────────────────── */}
        <div
          data-testid="dropzone"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragging ? "border-gray-900 bg-gray-50" : "border-gray-300 bg-white"
          }`}
        >
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">Drag and drop your .md files here</p>
          <p className="text-sm text-gray-400">
            Supports <span className="font-medium text-violet-600">SUPREME</span> format (━━━ dividers) and{" "}
            <span className="font-medium text-gray-600">V2</span> format (## headers)
          </p>
        </div>

        {/* ── Filters ──────────────────────────────────────────────────────── */}
        <Card className="p-5 bg-white border border-gray-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search by company, contact, industry or location…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-gray-300"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="status-filter" className="w-full md:w-44 border-gray-300">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="Follow-up">Follow-up</SelectItem>
                <SelectItem value="Qualified">Qualified</SelectItem>
                <SelectItem value="Won">Won</SelectItem>
                <SelectItem value="Lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <Select value={formatFilter} onValueChange={setFormatFilter}>
              <SelectTrigger data-testid="format-filter" className="w-full md:w-44 border-gray-300">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Formats</SelectItem>
                <SelectItem value="supreme">SUPREME only</SelectItem>
                <SelectItem value="v2">V2 only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* ── Leads Table ──────────────────────────────────────────────────── */}
        <Card className="bg-white border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funding</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outreach</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => {
                  const tier = lead.tags?.find(t => t.startsWith('TIER'));
                  const isSupreme = lead.lead_format === 'supreme';
                  return (
                    <tr
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">{lead.company_name}</span>
                          {tier && (
                            <Badge className={`${TIER_COLORS[tier] || 'bg-gray-100 text-gray-700 border-gray-300'} border text-xs px-1.5 py-0`}>
                              {tier}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-sm text-gray-500">{lead.company_size}</span>
                          {lead.location && (
                            <span className="flex items-center gap-0.5 text-xs text-gray-400">
                              <MapPin className="w-3 h-3" />
                              {lead.location}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{lead.contact_name}</div>
                        <div className="text-xs text-gray-500">{lead.designation}</div>
                        {isSupreme && lead.email && (
                          <div className="text-xs text-blue-500 mt-0.5 truncate max-w-[160px]">{lead.email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{lead.industry}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700">{lead.funding_amount || "—"}</div>
                        {isSupreme && lead.funding_date && (
                          <div className="text-xs text-gray-400">{lead.funding_date}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={`${STATUS_COLORS[lead.status]} border`}>
                          {lead.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const indicator = getOutreachIndicator(outreachSummary[lead.id]);
                          if (!indicator) {
                            return <span className="text-xs text-gray-400">—</span>;
                          }
                          const Icon = indicator.icon;
                          return (
                            <Badge className={`${indicator.className} border inline-flex items-center gap-1 text-xs font-medium`}>
                              <Icon className="w-3 h-3" />
                              {indicator.label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{lead.created_date}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            data-testid={`view-lead-${lead.id}`}
                            size="sm"
                            variant="outline"
                            className="border-gray-300"
                            onClick={() => navigate(`/lead/${lead.id}`)}
                          >
                            View
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                data-testid={`delete-lead-${lead.id}`}
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Lead</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete {lead.company_name}? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteLead(lead.id, lead.company_name)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredLeads.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No leads found. Upload some files to get started!</p>
              </div>
            )}
          </div>
        </Card>
    </div>
  );
};

export default Dashboard;
